import React from 'react';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import {withGlobalize, WithGlobalizeProps} from 'react-native-globalize';
import BackSubheader from '../../elements/BackSubheader';
import AVTextInput from '../../elements/AVTextInput';
import ItemAddedFeedback from '../../elements/cart/ItemAddedFeedback';
import Styles from '../../Styles';
import {Platform, StyleSheet, Switch, View, ScrollView} from 'react-native';
import UIManager from '../../elements/ui/UIManager';
import NavActions from 'src/actions/NavActions';
import ScreenContext from '../../ScreenContext';
import Localized from 'src/constants/AppStrings';
import AVText from '../../elements/AVText';
import RoundedButton, {ButtonType} from '../../elements/RoundedButton';
import {isValidEmail} from 'src/services/ValidatorService';
import KeyboardAvoidingView from '../../elements/365KeyboardAvoidingView';
import StickerList from '../../elements/sendSnack/StickerList';
import ActionsFactory from 'src/actions/ActionsFactory';
import AccountStore from 'src/stores/AccountStore';
import HttpClient from 'src/api/HttpClient';
import Util from 'src/Util';
import Settings from 'src/Settings';
import AppRoutes from 'src/AppRoutes';
import Events from 'src/logging/Events';
import {alertSuccess, alertError} from '../../helpers/AlertHelper';

type SendSnackScreenState = {
  amount: string;
  description: string;
  email: string;
  isPrivate: boolean;
  stickerId: number;
  amountError?: string;
  mailError?: string;
  isSnackDataValid: boolean;
  isGiftTextInputFocused: boolean;
};

const dollarSymbol = '$';

class SendSnackScreen extends React.Component<
  WithGlobalizeProps,
  SendSnackScreenState
> {
  static contextType = ScreenContext;

  constructor(props) {
    super(props);
    this.state = {
      amount: '',
      description: '',
      email: '',
      isPrivate: false,
      stickerId: 0,
      amountError: '',
      mailError: '',
      isSnackDataValid: false,
      isGiftTextInputFocused: false,
    };
    this.onBackSelect = this.onBackSelect.bind(this);
    this.getRightView = this.getRightView.bind(this);
    this.getBalanceContainer = this.getBalanceContainer.bind(this);
    this.onChangeEmail = this.onChangeEmail.bind(this);
    this.validateEmail = this.validateEmail.bind(this);
    this.onChangeGiftAmount = this.onChangeGiftAmount.bind(this);
    this.validateGiftAmount = this.validateGiftAmount.bind(this);
    this.onSendSnack = this.onSendSnack.bind(this);
    this.validateSnackData = this.validateSnackData.bind(this);
    this.handleSnackBalanceCovers = this.handleSnackBalanceCovers.bind(this);
    this.navigateToFunding = this.navigateToFunding.bind(this);
    this.onContactSelected = this.onContactSelected.bind(this);
    this.getGiftAmountTextValue = this.getGiftAmountTextValue.bind(this);
  }

  getBalanceContainer(smallText: boolean) {
    return UIManager.getBalanceContainer(smallText, Localized);
  }

  getRightView() {
    if (Platform.OS === 'web') {
      return null;
    } else {
      return (
        <View style={styles.rightContainer}>
          {this.getBalanceContainer(false)}
        </View>
      );
    }
  }

  onBackSelect() {
    NavActions.pop();
  }

  onChangeEmail(email: string) {
    this.setState({
      email,
      mailError: undefined,
    });
    this.validateSnackData(
      Number(this.state.amount),
      email,
      this.state.stickerId,
    );
  }

  validateEmail() {
    if (isValidEmail(this.state.email)) {
      this.setState({
        mailError: undefined,
      });
    } else {
      this.setState({
        mailError: Localized.Errors.please_enter_the_valid_email,
      });
    }
  }

  onChangeGiftAmount(amount: string) {
    amount = amount.replace(dollarSymbol, '');

    if (!amount || amount.match(/^\d{1,}(\.\d{0,2})?$/)) {
      this.setState({
        amount,
        amountError: undefined,
      });
    }

    this.validateSnackData(
      Number(amount),
      this.state.email,
      this.state.stickerId,
    );
  }

  validateGiftAmount() {
    this.setState({
      isGiftTextInputFocused: false,
    });
    const amountValue = Number(this.state.amount);

    if (amountValue >= 1.0) {
      this.setState({
        amount: amountValue.toFixed(2).toString(),
        amountError: undefined,
      });
    } else {
      this.setState({
        amountError: Localized.Errors.minimum_snack_gift_amount,
      });
    }
  }

  getGiftAmountTextValue() {
    if (this.state.amount === '' && this.state.isGiftTextInputFocused) {
      return dollarSymbol;
    } else if (this.state.amount === '') {
      return '';
    } else {
      return dollarSymbol + this.state.amount;
    }
  }

  onContactSelected(email: string) {
    this.setState({
      email,
    });

    if (isValidEmail(email)) {
      this.setState({
        mailError: undefined,
      });
    } else {
      this.setState({
        mailError: Localized.Errors.please_enter_the_valid_email,
      });
    }

    this.validateSnackData(
      Number(this.state.amount),
      email,
      this.state.stickerId,
    );
  }

  validateSnackData(amount: number, email: string, stickerId: number) {
    const validData =
      isValidEmail(email) && Number(amount) >= 1.0 && stickerId > 0;
    this.setState({
      isSnackDataValid: validData,
    });
  }

  handleSnackBalanceCovers() {
    const transDate = Util.getCurrentDate();
    if (this.state.email === AccountStore.getEmail()) {
      alertError(
        Localized.Errors.you_cannot_snack_to_yourself,
        null,
        undefined,
        Localized.Errors.oops,
      );
      return;
    }

    const snackAmount = Number(this.state.amount);
    const balance = AccountStore.getAccountBalance();
    const amountDue = snackAmount - balance;

    if (amountDue > 0) {
      this.navigateToFunding(amountDue, transDate);
    } else {
      this.onSendSnack(transDate);
    }
  }

  navigateToFunding(balance: number, transDate: string) {
    this.context.actions.navigateToFunding(true, {
      transDate,
      remainingDue: balance,
      isSnackGiftDue: true,
      addFundsSuccess: () => this.onSendSnack(transDate),
    });
  }

  async onSendSnack(transDate: string) {
    this.context.actions.showSpinner();

    try {
      const response = await ActionsFactory.getAccountActions().sendSnackToUser(
        AccountStore.getLocationId(),
        Number(this.state.amount),
        this.state.description,
        this.state.email,
        this.state.isPrivate,
        AccountStore.getAccountId(),
        this.state.stickerId,
        transDate,
        AccountStore.getAccountBalanceId(),
        Settings.buildType,
      );

      if (response) {
        NavActions.pop();
        alertSuccess(
          Localized.Success.formatString(
            Localized.Success.snack_sent_success,
            response.amount.toFixed(2).toString(),
            response.receiverName,
          ),
        );
      }
    } catch (err) {
      const errorCode = HttpClient.parseJsonSafe(
        err?.status ?? err?.statusCode,
      );

      if (errorCode === 404) {
        alertError(
          Localized.Errors.person_cannot_receive_snack_error,
          null,
          undefined,
          Localized.Errors.unable_to_send,
        );
      } else {
        alertError(Localized.Errors.error);
        Events.Error.trackEvent(
          'Exception',
          'SendSnackScreen:onSendSnack',
          err.message ? err.message : err.toString(),
        );
      }
    } finally {
      this.context.actions.hideSpinner();
    }
  }

  render() {
    const rightView = this.getRightView();
    const content = (
      <KeyboardAvoidingView
        style={Styles.Style.flex}
        behavior="height"
        insideTab
      >
        <ScrollView
          contentContainerStyle={Styles.Style.flex}
          keyboardDismissMode="interactive"
          automaticallyAdjustContentInsets={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={[styles.content, Styles.Style.maxWidthContainer]}>
            <AVText style={styles.chooseStickerLabel}>
              {Localized.Labels.choose_a_sticker}
            </AVText>
            <StickerList
              size={Styles.Heights.h8}
              onStickerSelection={(id) => {
                this.setState({
                  stickerId: id,
                });
                this.validateSnackData(
                  Number(this.state.amount),
                  this.state.email,
                  id,
                );
              }}
            />
            <AVTextInput
              label={Localized.Labels.gift_amount}
              onChangeText={(e) => this.onChangeGiftAmount(e)}
              accessibilityLabel="Gift Amount"
              value={this.getGiftAmountTextValue()}
              autoCapitalize="none"
              maxLength={10}
              onFocus={() =>
                this.setState({
                  isGiftTextInputFocused: true,
                })
              }
              onBlur={this.validateGiftAmount}
              returnKeyType="next"
              keyboardType={Platform.OS === 'web' ? 'default' : 'numeric'}
            />
            <AVText
              style={styles.errorLabel}
              accessibilityLabel="Snack Amount Error"
            >
              {this.state.amountError}
            </AVText>
            <AVTextInput
              label={Localized.Labels.your_message}
              onChangeText={(e) => {
                this.setState({
                  description: e,
                });
              }}
              accessibilityLabel="Your Message"
              value={this.state.description}
              autoCapitalize="none"
              clearButtonMode="while-editing"
              maxLength={140}
              returnKeyType="next"
            />
            <AVText style={styles.descriptionCountLabel}>
              {this.state.description.length + '/140'}
            </AVText>
            <View style={styles.contactContainer}>
              <AVText style={styles.toLabel}>{Localized.Labels.to}</AVText>
              <RoundedButton
                buttonType={ButtonType.outline}
                text={Localized.Labels.choose_from_contacts}
                containerStyle={styles.button}
                disabled={Platform.OS === 'web'}
                onPress={() => {
                  NavActions.navigate(AppRoutes.ChooseContact, {
                    onContactSelect: (email) => {
                      this.onContactSelected(email);
                    },
                  });
                }}
                accessibilityLabel="Choose from contacts"
              />
            </View>
            <AVTextInput
              label={Localized.Labels.email_address}
              onChangeText={(e) => {
                this.onChangeEmail(e);
              }}
              accessibilityLabel="Email"
              value={this.state.email}
              autoCapitalize="none"
              keyboardType="email-address"
              onBlur={this.validateEmail}
              returnKeyType="next"
              maxLength={75}
            />
            <AVText style={styles.errorLabel}>{this.state.mailError}</AVText>
            <View style={styles.privateStatusContainer}>
              <AVText style={styles.privateLabel}>
                {Localized.Labels.private}
              </AVText>
              <Switch
                accessibilityLabel="Snack Private Status"
                nativeID="Snack Private Status"
                value={this.state.isPrivate}
                onValueChange={(value) =>
                  this.setState({
                    isPrivate: value,
                  })
                }
                trackColor={{
                  false: Styles.auxColor,
                  true: Styles.primaryColor,
                }}
              />
            </View>
            <View style={styles.spacer} />
          </View>
        </ScrollView>
        <RoundedButton
          buttonType={ButtonType.action}
          onPress={this.handleSnackBalanceCovers}
          accessibilityLabel="Send Snack"
          disabled={!this.state.isSnackDataValid}
          text={Localized.Buttons.send_snack}
        />
      </KeyboardAvoidingView>
    );
    return (
      <BackSubheader
        title={Localized.Labels.send_a_snack_title}
        onBackSelect={this.onBackSelect}
        pop={false}
        rightView={rightView}
      >
        {content}
        <ItemAddedFeedback
          strings={Localized}
          extraMargin={Styles.Spacing.m0}
        />
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    paddingHorizontal: Styles.Spacing.m3,
    paddingTop: Styles.Spacing.m1,
  },
  rightContainer: {
    alignItems: 'flex-end',
    flex: 1,
    justifyContent: 'space-between',
    paddingTop: Styles.Spacing.m4,
  },
  errorLabel: {
    color: Styles.dangerColor,
    fontSize: Styles.Fonts.f1,
    alignSelf: 'flex-start',
  },
  descriptionCountLabel: {
    color: Styles.gray40,
    fontSize: Styles.Fonts.f0,
    alignSelf: 'flex-end',
  },
  textField: {
    marginTop: Styles.Spacing.m2,
    alignSelf: 'stretch',
    height: Styles.Heights.h3,
  },
  button: {
    alignSelf: 'flex-end',
  },
  privateStatusContainer: {
    flexDirection: 'row',
    alignSelf: 'flex-end',
    alignItems: 'center',
    padding: Styles.Spacing.m2,
  },
  privateLabel: {
    color: Styles.gray40,
    fontSize: Styles.Fonts.f1,
    marginRight: Styles.Spacing.m2,
  },
  chooseStickerLabel: {
    color: Styles.gray40,
    fontSize: Styles.Fonts.f0,
    marginTop: Styles.Spacing.m2,
  },
  contactContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: Styles.Spacing.m3,
    alignItems: 'center',
  },
  toLabel: {
    fontWeight: 'bold',
  },
  spacer: {
    height: Styles.Heights.h5,
  },
});
export default withForwardedNavigationParams()(withGlobalize(SendSnackScreen));
